import { CardHeader, Badge, IconButton, Menu } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { tss } from 'tss-react/mui';

import { useState } from 'react';

import { format } from 'date-fns';

import { UserAvatar } from '@/modules/user/components/UserAvatar';

import { graphql, type DocumentType } from '@/gql';

import { Or2Avatar } from '@/components/Avatar/Or2Avatar';

import { SendArticleAsEmailMenuItem } from './SendArticleAsEmailMenuItem';
import { ShareArticleToNoticeboardMenuItem } from './header/ShareArticleToNoticeboardMenuItem';

const NEWS_ARTICLE_HEADER_FRAGMENT = graphql(`
  fragment newsArticleHeaderFields on Article {
    ...shareArticleToNoticeboardMenuItemArticleFields
    ...sendArticleAsEmailMenuItemArticleFields
    _id
    title
    date
    url
    link
    type
    favicon_url
    provider_name
    feed {
      _id
      name
      owner {
        ... on Group {
          _id
          machineName
          iconUrl
        }
        ... on Service {
          _id
          machineName
          iconUrl
        }
      }
      source
    }
    lastModifiedBy {
      _id
      firstName
      lastName
      ...userImageFields
    }
  }
`);

interface Props {
  article: DocumentType<typeof NEWS_ARTICLE_HEADER_FRAGMENT>;
  showProvider?: boolean;
}

const useStyles = tss.create(({ theme }) => {
  return {
    groupAvatar: {
      width: 22,
      height: 22,
      border: `2px solid ${theme.palette.background.paper}`,
      backgroundColor: theme.palette.background.paper,
    },
  };
});

export const NewsArticleHeader = ({ article, showProvider }: Props) => {
  const { feed, lastModifiedBy } = article ?? {};

  const { owner: feedOwner } = feed || {};

  const isUserCreated = Boolean(lastModifiedBy) && !feed?.source;

  const date = article?.date
    ? format(new Date(article?.date), "d MMMM y 'at' h:mm a")
    : null;

  // const [shareOpen, setShareOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { classes } = useStyles();

  const handleClose = () => {
    // setShareOpen(false);
    setAnchorEl(null);
  };

  return (
    <CardHeader
      avatar={
        isUserCreated && lastModifiedBy?._id ? (
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              showProvider ? (
                <Or2Avatar
                  src={feedOwner?.iconUrl || undefined}
                  alt={`${feed?.name} logo`}
                  className={classes.groupAvatar}
                >
                  {feed?.name?.charAt(0)}
                </Or2Avatar>
              ) : null
            }
          >
            <UserAvatar user={lastModifiedBy} size={32} />
          </Badge>
        ) : (
          <Or2Avatar
            src={article.favicon_url || undefined}
            alt={article.provider_name || ''}
          >
            {article?.provider_name?.charAt(0)}
          </Or2Avatar>
        )
      }
      action={
        <>
          <IconButton
            aria-label="actions"
            aria-controls={`actions-menu-${article._id}`}
            aria-haspopup="true"
            onClick={(event) => {
              setAnchorEl(event.currentTarget);
            }}
            size="large"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id={`actions-menu-${article._id}`}
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <ShareArticleToNoticeboardMenuItem
              article={article}
              onClose={handleClose}
            />
            {isUserCreated ? (
              <SendArticleAsEmailMenuItem
                article={article}
                onClose={handleClose}
              />
            ) : null}
          </Menu>
        </>
      }
      title={`${
        isUserCreated
          ? lastModifiedBy?.firstName + ' ' + lastModifiedBy?.lastName
          : ''
      } ${showProvider ? String(article.provider_name || feed?.name) : ''}`}
      subheader={date}
    />
  );
};
